import type { AddedList } from "../data/store.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icons.js";
import { addedPath } from "../lib/router.js";
import { actionsButton, type ActionMenu, type MenuItem } from "./menu.js";
import { SHARED_WORDS, sharedPhrase } from "./sharedPage.js";
import { KIND_ICON, type Shell } from "./ui.js";

/**
 * Lists' "Shared with me" group (g132): the shared lists the account added,
 * below its own. Each opens inside the app (g137), by its grant id and the
 * session, and offers one thing: Remove from my Lists. Nothing
 * here is the owner's to offer, so there is no handle, no Move up or down,
 * no Share, rename or delete, and none of these lists is in the store's own,
 * so Today, Upcoming, counts and Reorder lists never see them.
 */

/** The group's words, exported so the tests pin them. */
export const SHARED_WITH_ME = {
  heading: "Shared with me",
  ended: "No longer shared",
  unnamed: "A shared list",
  remove: SHARED_WORDS.removeFromLists,
  removed: (name: string) => `Removed ${name} from your Lists.`,
} as const;

export interface SharedWithMeOptions {
  /**
   * The screen the group ends, while it has any entry. An account that added
   * nothing gets a Lists screen exactly as it was, ending with its add row.
   */
  parent: HTMLElement;
  /** The Lists screen's one menu, so only one is ever open. */
  menu: ActionMenu;
  /** Where focus goes when the last row goes. */
  landing: () => HTMLElement;
  /** Called before a row's menu opens, so Reorder lists ends first. */
  leaving: () => void;
}

export function sharedWithMe(shell: Shell, options: SharedWithMeOptions): { dispose(): void } {
  const { store } = shell;
  const rows = el("ul", { class: "rows added-rows" });
  const node = el("section", {
    class: "added-group",
    attrs: { "aria-labelledby": "added-heading" },
    children: [el("h2", { class: "group-head", attrs: { id: "added-heading" }, text: SHARED_WITH_ME.heading }), rows],
  });

  const nameOf = (entry: AddedList) => entry.name ?? SHARED_WITH_ME.unnamed;
  const access = (entry: AddedList) => (entry.mode === "edit" ? SHARED_WORDS.edit : SHARED_WORDS.view);

  const remove = (entry: AddedList, rowNode: HTMLElement) => {
    // The row below, or above, or the way out of the group, as a removed list's row does.
    const near = (rowNode.nextElementSibling ?? rowNode.previousElementSibling) as HTMLElement | null;
    const landing = near?.dataset.grant;
    void store.removeAddedList(entry.grantId);
    // The group has been drawn again by now, so the row is found afresh.
    const target = landing ? rows.querySelector<HTMLElement>(`[data-grant="${landing}"] .item-actions`) : null;
    (target ?? options.landing()).focus({ preventScroll: true });
    shell.announce(SHARED_WITH_ME.removed(nameOf(entry)));
  };

  const row = (entry: AddedList): HTMLElement => {
    const glyph = icon(entry.kind ? KIND_ICON[entry.kind] : "list");
    const meta = entry.state === "live"
      ? `${sharedPhrase(entry.sharedBy ? { name: entry.sharedBy } : "unnamed")} · ${access(entry)}`
      : SHARED_WITH_ME.ended;
    const body = el("div", { class: "row-body", children: [
      el("bdi", { class: "row-name", text: nameOf(entry) }),
      el("span", { class: "row-kind", text: meta }),
    ] });
    const label = `${nameOf(entry)}, ${meta.replace(" · ", ", ")}`;
    // A live entry opens inside the app, as the account's own lists do (g137).
    const main = entry.state === "live"
      ? el("a", { class: "row", attrs: { href: addedPath(entry.grantId), "data-link": true, "aria-label": label },
        children: [el("span", { class: "list-kind", children: [glyph] }), body] })
      : el("div", { class: "row added-ended", children: [el("span", { class: "list-kind", children: [glyph] }), body] });
    const actions = actionsButton();
    actions.setAttribute("aria-label", `Actions for ${nameOf(entry)}`);
    // The first column is a handle's on the rows above: kept empty, so the names line up, and closed when theirs is (g136).
    const rowNode = el("li", { class: "list-row added-row", attrs: { "data-grant": entry.grantId }, children: [
      el("span", { class: "added-spacer", attrs: { "aria-hidden": "true" } }), main, actions,
    ] });
    const entries = (): MenuItem[] => [{ label: SHARED_WITH_ME.remove, icon: "trash", onSelect: () => remove(entry, rowNode) }];
    actions.addEventListener("click", () => {
      options.leaving();
      options.menu.open(actions, entries);
    });
    return rowNode;
  };

  /** The whole group again: it is small, and every reply sends it whole. Focus inside it stays on the same row's control. */
  let painted: string | undefined;
  const paint = (entries: AddedList[]) => {
    // Every account reply sends the set, mostly unchanged: only a change redraws it.
    const said = JSON.stringify(entries);
    if (said === painted) return;
    painted = said;
    const focused = rows.contains(document.activeElement) ? document.activeElement : undefined;
    const keep = focused?.closest<HTMLElement>("[data-grant]")?.dataset.grant;
    const control = focused?.classList.contains("item-actions") ? ".item-actions" : ".row";
    if (options.menu.isOpen() && rows.contains(options.menu.anchor() ?? null)) options.menu.close();
    rows.replaceChildren(...entries.map(row));
    if (entries.length === 0) node.remove();
    else if (!node.isConnected) options.parent.append(node);
    if (keep) rows.querySelector<HTMLElement>(`[data-grant="${keep}"] ${control}`)?.focus({ preventScroll: true });
  };

  paint(store.addedLists());
  const stop = store.onAddedLists(paint);
  return { dispose: stop };
}
